import React, { useState, useCallback } from 'react';
import { useSalon } from '../../../contexts/SalonContext';
import { PhotoIcon, EyeIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { uploadSalonFile, deleteSalonFile } from '../../../firebase/storage';

const SalonVisualSettings = () => {
  const { salonConfig, updateSalon, salonId } = useSalon();
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Estados para as configurações visuais
  const [visualConfig, setVisualConfig] = useState({
    primaryColor: salonConfig?.primaryColor || '#000000',
    fontFamily: salonConfig?.fontFamily || "'Poppins', sans-serif",
    logoUrl: salonConfig?.logoUrl || '',
    headerStyle: salonConfig?.headerStyle || 'logo-text'
  });

  // Lista de fontes disponíveis
  const availableFonts = [
    { name: 'Poppins', value: "'Poppins', sans-serif" },
    { name: 'Roboto', value: "'Roboto', sans-serif" },
    { name: 'Open Sans', value: "'Open Sans', sans-serif" },
    { name: 'Montserrat', value: "'Montserrat', sans-serif" },
    { name: 'Lato', value: "'Lato', sans-serif" },
  ];

  // Opções de estilo do cabeçalho
  const headerStyleOptions = [
    { value: 'logo-text', label: 'Logo e Texto' },
    { value: 'logo-only', label: 'Apenas Logo' },
    { value: 'text-only', label: 'Apenas Texto' }
  ];

  // Função para atualizar a cor primária
  const handleColorChange = (e) => {
    setVisualConfig(prev => ({
      ...prev,
      primaryColor: e.target.value
    }));
  };

  // Função para atualizar a fonte
  const handleFontChange = (e) => {
    setVisualConfig(prev => ({
      ...prev,
      fontFamily: e.target.value
    }));
  };

  // Função para lidar com o upload do logo
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      setSelectedFile(file);

      // Se já existe um logo, remover o antigo usando o path do storage
      if (visualConfig.logoPath) {
        console.log('Removendo logo antigo usando path:', visualConfig.logoPath);
        await deleteSalonFile(visualConfig.logoPath);
      } else if (visualConfig.logoUrl) {
        // Fallback para logos antigos que não têm o path armazenado
        console.log('Tentando remover logo antigo usando URL:', visualConfig.logoUrl);
        await deleteSalonFile(visualConfig.logoUrl);
      }

      // Fazer upload do novo logo
      console.log('Iniciando upload do novo logo');
      const { url: downloadURL, path: storagePath } = await uploadSalonFile(salonId, file, 'logo');
      console.log('Logo enviado com sucesso. URL:', downloadURL, 'Path:', storagePath);

      // Atualizar o estado e salvar no banco de dados
      const newConfig = {
        ...visualConfig,
        logoUrl: downloadURL,
        logoPath: storagePath // Armazenar o caminho para facilitar remoção futura
      };
      
      console.log('Atualizando configuração do salão com novo logo');
      setVisualConfig(newConfig);
      await updateSalon(newConfig);

      toast.success('Logo atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error);
      toast.error('Erro ao atualizar o logo');
    } finally {
      setLoading(false);
    }
  };

  // Função para salvar as configurações
  const handleSave = async () => {
    try {
      setLoading(true);
      await updateSalon(visualConfig);
      toast.success('Configurações visuais atualizadas com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar configurações:', error);
      toast.error('Erro ao atualizar configurações');
    } finally {
      setLoading(false);
    }
  };

  // Função para abrir a visualização em nova aba
  const handlePreview = () => {
    if (!salonId) {
      toast.error('Não foi possível abrir a visualização');
      return;
    }
    window.open(`/${salonId}`, '_blank');
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Configurações Visuais</h2>
        <button
          onClick={handlePreview}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <EyeIcon className="h-5 w-5 mr-2" />
          Ver como cliente
        </button>
      </div>
      
      {/* Seção de Logo */}
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-4">Logo do Salão</h3>
        <div className="flex items-center space-x-4">
          <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden">
            {visualConfig.logoUrl ? (
              <img 
                src={visualConfig.logoUrl} 
                alt="Logo do salão" 
                className="w-full h-full object-contain"
              />
            ) : (
              <PhotoIcon className="w-12 h-12 text-gray-400" />
            )}
          </div>
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              id="logo-upload"
              disabled={loading}
            />
            <label
              htmlFor="logo-upload"
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? 'Enviando...' : 'Escolher Logo'}
            </label>
            <p className="mt-2 text-sm text-gray-500">
              Recomendado: PNG ou SVG com fundo transparente
            </p>
          </div>
        </div>
      </div>

      {/* Seção de Cor */}
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-4">Cor do Salão</h3>
        <div className="flex items-center space-x-4 bg-gray-50 p-4 rounded-lg">
          <input
            type="color"
            value={visualConfig.primaryColor}
            onChange={handleColorChange}
            className="h-12 w-12 p-0 border-0 rounded cursor-pointer"
          />
          <div className="flex-1 h-3 rounded" style={{ backgroundColor: visualConfig.primaryColor }} />
        </div>
      </div>

      {/* Seção de Fonte */}
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-4">Fonte Principal</h3>
        <select
          value={visualConfig.fontFamily}
          onChange={handleFontChange}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        >
          {availableFonts.map((font) => (
            <option key={font.value} value={font.value}>
              {font.name}
            </option>
          ))}
        </select>
      </div>

      {/* Seção de Estilo do Cabeçalho */}
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-4">Estilo do Cabeçalho</h3>
        <div className="space-y-2">
          {headerStyleOptions.map((option) => (
            <label key={option.value} className="flex items-center">
              <input
                type="radio"
                value={option.value}
                checked={visualConfig.headerStyle === option.value}
                onChange={(e) => setVisualConfig(prev => ({
                  ...prev,
                  headerStyle: e.target.value
                }))}
                className="mr-2"
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      {/* Botão de Salvar */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>
    </div>
  );
};

export default SalonVisualSettings; 